import React, { useState } from 'react';

import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faQuestionCircle,
  faTimes
} from '@fortawesome/pro-solid-svg-icons';
import { OverlayTrigger, Popover, Alert as BsAlert } from 'react-bootstrap-5';

type ALERT_VARIANT = 'danger' | 'info' | 'success' | 'warning';

type AlertProps = {
  alertClass?: string;
  alertIconClass?: string;
  alertIconSize?: FontAwesomeIconProps['size'];
  alertIconType?: string;
  content: string | React.ReactNode;
  dismissOnClick?: boolean;
  handleDismiss?: () => void;
  infoIconClass?: string;
  infoIconSize?: FontAwesomeIconProps['size'];
  infoMessage?: string | React.ReactNode;
  variant?: ALERT_VARIANT;
};

export default function Alert({
  alertClass,
  alertIconClass,
  alertIconType,
  alertIconSize = 'lg',
  content,
  dismissOnClick = false,
  handleDismiss,
  infoIconClass,
  infoIconSize = 'lg',
  infoMessage,
  variant = 'info',
}: AlertProps) {
  const [show, setShow] = useState(true);
  const selectIcon = (iconType: string | undefined) => {
    switch (iconType) {
    case 'danger':
      return faExclamationTriangle;
    case 'error':
      return faTimes;
    case 'info':
      return faInfoCircle;
    case 'question':
      return faQuestionCircle;
    case 'success':
      return faCheckCircle;
    default:
      return null;
    }
  };

  const renderIcon = (
    iconClass: string | undefined,
    iconType: string | undefined,
    iconName: string | undefined,
    iconSize: FontAwesomeIconProps['size']
  ) => {
    const selectedIcon = selectIcon(iconType);

    if (!selectedIcon) return null;

    return (
      <FontAwesomeIcon
        className={iconClass}
        icon={selectedIcon}
        name={iconName}
        size={iconSize}
      />
    );
  };

  const renderAlertIcon = () => {
    const icon = renderIcon(alertIconClass, alertIconType, 'alert-icon', alertIconSize);

    if (!icon) return null;

    return <div className="align-self-center px-3">{icon}</div>;
  };

  const renderMoreInfo = () => {
    const icon = renderIcon(infoIconClass, 'question', 'info-icon', infoIconSize);

    if (!icon || !infoMessage) return null;

    const overlay = (
      <Popover className="max-full-width" id="info-description">
        <Popover.Body>
          {infoMessage}
        </Popover.Body>
      </Popover>
    );

    return (
      <div className="align-self-center px-3" style={{ marginLeft: 'auto' }}>
        <OverlayTrigger overlay={overlay} placement="top">{icon}</OverlayTrigger>
      </div>
    );
  };

  const dismissAction = () => {
    if (handleDismiss != undefined) {
      return handleDismiss;
    } else if (dismissOnClick == true) {
      return () => {
        setShow(false);
      };
    }
  };

  return show ? (
    <BsAlert
      className={alertClass}
      variant={variant}
      onClose={dismissAction()}
    >
      <div className="d-flex">
        {renderAlertIcon()}
        {content}
        {renderMoreInfo()}
      </div>
    </BsAlert>
  ) : null;
}
